// Utility to fetch RTI design system colors from CSS variables for charts
export const getCssColor = (varName: string, fallback?: string): string => {
  if (typeof window === 'undefined') return fallback || '#888';
  try {
    const value = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
    return value || fallback || '#888';
  } catch {
    return fallback || '#888';
  }
};

export const withAlpha = (color: string, alpha: number): string => {
  const hex = color.trim();
  if (hex.startsWith('#') && (hex.length === 7 || hex.length === 4)) {
    const a = Math.round(alpha * 255).toString(16).padStart(2, '0');
    return `${hex}${a}`;
  }
  const match = hex.match(/^(oklch|hsl|rgb)\(/i);
  if (match) {
    const base = hex.endsWith(')') ? hex.slice(0, -1) : hex;
    return `${base} / ${alpha})`;
  }
  return `rgba(128, 237, 64, ${alpha})`;
};

export const getRsColors = () => ({
  gray: getCssColor('--color-rs-highlight-gray'),
  green: getCssColor('--color-rs-green', '#80ed40'),
  lightGreen: getCssColor('--color-rs-light-green', '#7ac950'),
  black: getCssColor('--color-rs-black'),
  offBlack: getCssColor('--color-rs-off-black'),
  jet: getCssColor('--color-rs-jet'),
  jetLight: getCssColor('--color-rs-jet-light', '#777777'),
  red: getCssColor('--color-rs-red', '#ef4444'),
  salmon: getCssColor('--color-rs-salmon', '#fb7185'),
  purple: getCssColor('--color-rs-purple', '#8b5cf6'),
  lightPurple: getCssColor('--color-rs-light-purple', '#a78bfa'),
  darkGreen: getCssColor('--color-rs-dark-green'),
  muddyGreen: getCssColor('--color-rs-muddy-green', '#4d6b4d'),
  greenDash: getCssColor('--color-rs-green-dash-gradient-start', '#256d1b'),
  redDash: getCssColor('--color-rs-red-dash-gradient-start', '#b91c1c'),
  purpleDash: getCssColor('--color-rs-purple-dash-gradient-start', '#6d28d9'),
  plumDash: getCssColor('--color-rs-plum-dash-gradient-start', '#7e22ce'),
  rbEnd: getCssColor('--color-rs-rb-gradient-end', '#9333ea'),
  greenBorder: getCssColor('--color-rs-green-border'),
  offWhite: getCssColor('--color-rs-off-white', '#f3f4f6'),
  blueDash: getCssColor('--color-rs-blue-dash')
});

export const chartColors = (() => {
  const rs = getRsColors();
  // Fallback palette (stable hex values)
  const palette = [
    rs.green || '#80ed40',
    rs.purple || '#8b5cf6',
    rs.plumDash || '#7e22ce',
    rs.red || '#ef4444',
    rs.salmon || '#fb7185',
    rs.muddyGreen || '#4d6b4d',
    rs.blueDash || '#3b82f6',
    rs.lightPurple || '#a78bfa',
  ];

  const polymers = {
    PET: '#1f77b4',
    PP: '#ff7f0e',
    PE: '#2ca02c',
    PS: '#d62728',
    PVC: '#9467bd'
  } as const;

  const trends = {
    up: '#16a34a',
    down: '#dc2626',
    neutral: '#7a7a7a'
  } as const;

  const opacity = {
    light: 0.25,
    medium: 0.5,
    heavy: 0.85
  } as const;

  const toRgba = (color: string, alpha: number) => {
    const c = color.trim();
    // Hex (#rgb or #rrggbb)
    if (c.startsWith('#')) {
      const h = c.length === 4 ? c.slice(1).split('').map((x) => x + x).join('') : c.slice(1);
      const bigint = parseInt(h, 16);
      if (!Number.isNaN(bigint)) {
        return `rgba(${(bigint >> 16) & 255}, ${(bigint >> 8) & 255}, ${bigint & 255}, ${alpha})`;
      }
    }
    // oklch()/oklab()/hsl()/rgb()/etc — inject alpha via modern slash syntax
    if (/^(oklch|oklab|hsl|hwb|lab|lch|rgb|color)\(/i.test(c)) {
      const base = (c.endsWith(')') ? c.slice(0, -1) : c).split('/')[0].trimEnd();
      return `${base} / ${alpha})`;
    }
    return `rgba(128, 237, 64, ${alpha})`;
  };

  return {
    primary: rs.green || '#80ed40',
    secondary: rs.purpleDash || '#6d28d9',
    success: '#16a34a',
    warning: '#d97706',
    danger: rs.red || '#dc2626',
    neutral: rs.jet || '#565657',
    polymers,
    trends,
    opacity,
    palette,
    gradients: {
      primary: `linear-gradient(90deg, ${rs.greenDash || '#256d1b'}, ${rs.rbEnd || '#9333ea'})`,
      secondary: `linear-gradient(90deg, ${rs.purpleDash || '#6d28d9'}, ${rs.plumDash || '#7e22ce'})`
    },
    getColorByIndex: (index: number) => palette[index % palette.length],
    getPolymerColor: (polymer: string) => (polymers as any)[polymer] || (rs.green || '#80ed40'),
    getTrendColor: (trend: string) => (trends as any)[trend] || trends.neutral,
    withOpacity: (hex: string, alpha: number) => toRgba(hex, alpha)
  };
})();
